import { EmployeeListItem } from "@/components/employee/EmployeeListItem.tsx";
import { FileItem } from "@/components/files/FileItem.tsx";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { ApiField, ApiFieldTypes } from "@/types/custom-field.types.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { getJobProfileTitle } from "@/types/job-profile.types.ts";
import {
  ApiJob,
  getJobPriorityColorClasses,
  getJobPriorityTitle,
} from "@/types/recruit/job.types.ts";
import { FontAwesomeIcon, HtmlViewer, ListItem, Stack, Tag, Text } from "@skailer-com/uikit";
import { ReactNode } from "react";

export default function useJobCustomFieldsHook() {
  const { renderCurrency } = useFormatter();
  const { renderCustomFieldListItem, getFieldTitle } = useCustomFieldsHook();

  const renderTextListItem = (label: string, value: string | null | undefined) =>
    value ? <ListItem preTitle={label} title={value} /> : undefined;

  const renderHtmlListItem = (label: string, value: string | null) =>
    value ? (
      <ListItem emphasis={"middle"} preTitle={label} titleSlot={<HtmlViewer content={value} />} />
    ) : undefined;

  const renderEmployeesListItem = (label: string, employees: ApiEmployee[]) =>
    employees.length > 0 ? (
      <ListItem
        preTitle={label}
        titleSlot={
          <Stack gap={"sm"} className={"pt-2"}>
            {employees.map((employee) => (
              <EmployeeListItem
                key={employee.id}
                employee={employee}
                interactive={"page"}
                blank={true}
                behavior={"card"}
              />
            ))}
          </Stack>
        }
      />
    ) : undefined;

  const renderSalaryListItem = (
    label: string,
    value: number | null,
    currencyCode: string | null
  ) =>
    value != null ? (
      <ListItem preTitle={label} title={renderCurrency(value.toString(), currencyCode)} />
    ) : undefined;

  const renderJobFieldListItem = (
    field: ApiField,
    job: ApiJob,
    showCurrency: boolean
  ): ReactNode | undefined => {
    if (field.type === ApiFieldTypes.custom) {
      return renderCustomFieldListItem(field, job.custom_fields ?? []);
    }

    const label = getFieldTitle(field);
    const currencyCode = showCurrency ? (job.currency?.code ?? null) : null;

    switch (field.type) {
      case ApiFieldTypes.job_public_title:
        return renderTextListItem(label, job.public_title);
      case ApiFieldTypes.job_department:
        return renderTextListItem(label, job.department?.title);
      case ApiFieldTypes.job_division:
        return renderTextListItem(label, job.division?.title);
      case ApiFieldTypes.job_job_profile:
        return renderTextListItem(label, job.job_profile && getJobProfileTitle(job.job_profile));
      case ApiFieldTypes.job_location:
        return renderTextListItem(label, job.location?.title);
      case ApiFieldTypes.job_approval_policy:
        return renderTextListItem(label, job.approval_policy?.title);
      case ApiFieldTypes.job_currency:
        return renderTextListItem(label, job.currency?.name);
      case ApiFieldTypes.job_priority:
        return job.priority ? (
          <ListItem
            preTitle={label}
            titleSlot={
              <Text className={"font-semibold"}>
                <FontAwesomeIcon
                  icon={`fa-solid fa-circle text-sm mr-2 ${getJobPriorityColorClasses(job.priority).dotTextClass}`}
                />
                {getJobPriorityTitle(job.priority)}
              </Text>
            }
          />
        ) : undefined;
      case ApiFieldTypes.job_pipeline:
        return (
          <ListItem
            preTitle={label}
            title={job.pipeline.title}
            captionSlot={
              job.pipeline.stages.length > 0 && (
                <Stack direction={"horizontal"} gap={"sm"} className={"flex-wrap pt-2"}>
                  {job.pipeline.stages.map((stage) => (
                    <Tag key={stage.id} variant={"dark"}>
                      {stage.title}
                    </Tag>
                  ))}
                </Stack>
              )
            }
          />
        );
      case ApiFieldTypes.job_recruiters:
        return renderEmployeesListItem(label, job.recruiters);
      case ApiFieldTypes.job_stakeholders:
        return renderEmployeesListItem(label, job.stakeholders);
      case ApiFieldTypes.job_salary_min:
        return renderSalaryListItem(label, job.salary_min, currencyCode);
      case ApiFieldTypes.job_salary_max:
        return renderSalaryListItem(label, job.salary_max, currencyCode);
      case ApiFieldTypes.job_description:
        return renderHtmlListItem(label, job.description);
      case ApiFieldTypes.job_requirements:
        return renderHtmlListItem(label, job.requirements);
      case ApiFieldTypes.job_responsibilities:
        return renderHtmlListItem(label, job.responsibilities);
      case ApiFieldTypes.job_conditions:
        return renderHtmlListItem(label, job.conditions);
      case ApiFieldTypes.job_files:
        return job.files.length > 0 ? (
          <ListItem
            preTitle={label}
            titleSlot={
              <Stack gap={"sm"} className={"pt-2"}>
                {job.files.map((file) => (
                  <FileItem key={file.id} file={file} />
                ))}
              </Stack>
            }
          />
        ) : undefined;
      default:
        return undefined;
    }
  };

  return { renderJobFieldListItem };
}
